import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconBan,
  IconBell,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconFileInvoice,
  IconTrash,
  IconWritingSign,
} from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import {
  Group,
  IconAction,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastError } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";
import {
  contarMisResolucionesNoVistas,
  eliminarMiNotificacion,
  listarMisSolicitudesCatalogo,
  marcarMisSolicitudesVistas,
  type SolicitudDeCatalogo,
} from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";
import {
  contarMisNotificacionesNoVistas,
  listarMisNotificaciones,
  marcarMisNotificacionesVistas,
  ocultarMiNotificacion,
  type Notificacion,
  type TipoNotificacion,
} from "../services/notificaciones.api";

// --- Íconos por tipo (semánticos · mismos acentos que los estados) ---------------

const ICONO_CATALOGO = {
  Pendiente: <IconClock size={16} stroke={1.7} className="text-ambar-600" />,
  Atendida: <IconCircleCheck size={16} stroke={1.7} className="text-exito-600" />,
  Rechazada: <IconCircleX size={16} stroke={1.7} className="text-peligro-600" />,
} as const;

const ICONO_NOTIF: Record<TipoNotificacion, ReactNode> = {
  AutorizacionNivel: <IconWritingSign size={16} stroke={1.7} className="text-azulProfundo-600" />,
  AutorizacionCompleta: <IconCircleCheck size={16} stroke={1.7} className="text-exito-600" />,
  Rechazo: <IconCircleX size={16} stroke={1.7} className="text-peligro-600" />,
  Baja: <IconBan size={16} stroke={1.7} className="text-carmin-600" />,
  Contabilizacion: <IconFileInvoice size={16} stroke={1.7} className="text-exito-700" />,
};

// --- Modelo unificado del panel ---------------------------------------------------
// La campana fusiona DOS fuentes con contrato gemelo: el feed del CORE
// (autorizaciones/rechazos/bajas/contabilización de solicitudes de pago) y las
// resoluciones de solicitudes de catálogo del módulo Administracion.

interface ItemCampana {
  key: string;
  fecha: string;
  noVista: boolean;
  icono: ReactNode;
  contenido: ReactNode;
  navegar: (() => void) | null;
  eliminar: (() => void) | null;
  eliminando: boolean;
  etiquetaEliminar: string;
}

function TextoCatalogo({ s }: { s: SolicitudDeCatalogo }) {
  const sufijo =
    s.estado === "Pendiente"
      ? "sigue pendiente con el administrador."
      : s.estado === "Atendida"
        ? "fue ATENDIDA · el elemento ya está en el catálogo."
        : "fue RECHAZADA.";
  return (
    <>
      <Text size="sm" style={{ lineHeight: 1.35 }}>
        Tu solicitud de alta de {s.tipo.toLowerCase()} <Text span fw={600}>{s.clave}</Text> {sufijo}
      </Text>
      {s.estado !== "Pendiente" && s.respuestaComentario && (
        <Text size="xs" c="dimmed" truncate title={s.respuestaComentario}>
          {s.estado === "Rechazada" ? "Motivo: " : ""}
          {s.respuestaComentario}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        {dayjs(s.resueltaAt ?? s.creadoAt).format("DD/MM/YYYY HH:mm")}
        {s.numeroSolicitudPago ? ` · solicitud de pago #${s.numeroSolicitudPago}` : ""}
      </Text>
    </>
  );
}

function TextoNotificacion({ n }: { n: Notificacion }) {
  return (
    <>
      <Text size="sm" style={{ lineHeight: 1.35 }}>
        {n.mensaje}
      </Text>
      <Text size="xs" c="dimmed">
        {dayjs(n.creadaAt).format("DD/MM/YYYY HH:mm")}
      </Text>
    </>
  );
}

/**
 * Campana de notificaciones del header · panel ÚNICO con todos los tipos:
 * autorizaciones por nivel, autorización completa, rechazos, bajas y
 * contabilización (feed del CORE) + resoluciones de solicitudes de catálogo
 * (P38). El badge suma las NO vistas de ambas fuentes (polling 60s); abrir el
 * panel las marca vistas. Cada item navega a su solicitud.
 */
export function NotificacionesCampana() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: catalogoNoVistas } = useQuery({
    queryKey: qk.misNotificacionesCatalogo,
    queryFn: contarMisResolucionesNoVistas,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const { data: notifNoVistas } = useQuery({
    queryKey: qk.misNotificacionesCount,
    queryFn: contarMisNotificacionesNoVistas,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: catalogo, refetch: refetchCatalogo } = useQuery({
    queryKey: qk.misSolicitudesCatalogo,
    queryFn: listarMisSolicitudesCatalogo,
    enabled: false, // solo al abrir el panel
  });
  const { data: notificaciones, refetch: refetchNotificaciones } = useQuery({
    queryKey: qk.misNotificaciones,
    queryFn: listarMisNotificaciones,
    enabled: false, // solo al abrir el panel
  });

  const marcarVistasCatalogo = useMutation({
    mutationFn: marcarMisSolicitudesVistas,
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.misNotificacionesCatalogo }),
  });
  const marcarVistasNotif = useMutation({
    mutationFn: marcarMisNotificacionesVistas,
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.misNotificacionesCount }),
  });

  const alErrorEliminar = (error: unknown) => {
    toastError({ title: "No se pudo eliminar", message: extractApiMessage(error, "Intenta de nuevo.") });
  };
  const eliminarCatalogo = useMutation({
    mutationFn: eliminarMiNotificacion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.misNotificacionesCatalogo });
      void refetchCatalogo();
    },
    onError: alErrorEliminar,
  });
  const eliminarNotif = useMutation({
    mutationFn: ocultarMiNotificacion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.misNotificacionesCount });
      void refetchNotificaciones();
    },
    onError: alErrorEliminar,
  });

  const alAbrir = (opened: boolean) => {
    if (!opened) return;
    void refetchCatalogo();
    void refetchNotificaciones();
    if ((catalogoNoVistas ?? 0) > 0) marcarVistasCatalogo.mutate();
    if ((notifNoVistas ?? 0) > 0) marcarVistasNotif.mutate();
  };

  // --- Fusión de ambas fuentes · no vistas primero, luego recientes ---------------

  const items: ItemCampana[] = [
    ...(notificaciones ?? []).map((n): ItemCampana => ({
      key: `n-${n.id}`,
      fecha: n.creadaAt,
      noVista: !n.vistaAt,
      icono: ICONO_NOTIF[n.tipo],
      contenido: <TextoNotificacion n={n} />,
      navegar: () => void navigate({ to: "/solicitudes/$id", params: { id: n.solicitudId } }),
      eliminar: () => eliminarNotif.mutate(n.id),
      eliminando: eliminarNotif.isPending && eliminarNotif.variables === n.id,
      etiquetaEliminar: `Eliminar notificación de la solicitud #${n.numeroSolicitud}`,
    })),
    ...(catalogo ?? []).map((s): ItemCampana => ({
      key: `c-${s.id}`,
      fecha: s.resueltaAt ?? s.creadoAt,
      noVista: s.estado !== "Pendiente" && !s.vistaPorSolicitanteAt,
      icono: ICONO_CATALOGO[s.estado],
      contenido: <TextoCatalogo s={s} />,
      navegar: s.solicitudPagoId
        ? () =>
            void navigate({
              // Cada tipo de solicitud de catálogo se resuelve en un paso distinto del wizard:
              // proveedor → paso Proveedor · cuenta por clave → paso Prorrateo (donde vive la línea).
              to: s.tipo === "CuentaContablePorClave" ? "/solicitudes/$id/prorrateo" : "/solicitudes/$id/proveedor",
              params: { id: s.solicitudPagoId! },
            })
        : null,
      // Una pendiente es el recordatorio de que el trámite sigue abierto · no se borra.
      eliminar: s.estado !== "Pendiente" ? () => eliminarCatalogo.mutate(s.id) : null,
      eliminando: eliminarCatalogo.isPending && eliminarCatalogo.variables === s.id,
      etiquetaEliminar: `Eliminar notificación de ${s.clave}`,
    })),
  ].sort((a, b) => {
    if (a.noVista !== b.noVista) return a.noVista ? -1 : 1;
    return dayjs(b.fecha).valueOf() - dayjs(a.fecha).valueOf();
  });

  const totalNoVistas = (catalogoNoVistas ?? 0) + (notifNoVistas ?? 0);

  return (
    <Popover onOpenChange={alAbrir}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notificaciones"
          className="relative flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted"
        >
          <IconBell size={18} />
          {totalNoVistas > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-peligro-600 px-1 text-[10px] font-semibold text-white ring-2 ring-card">
              {totalNoVistas}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-95 p-1">
        <Group justify="space-between" className="px-2 py-1">
          <Text size="sm" fw={600}>
            Notificaciones
          </Text>
        </Group>
        <div className="max-h-90 overflow-y-auto">
          <Stack gap={4} className="p-1">
            {items.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" className="py-3">
                Sin notificaciones · aquí verás las autorizaciones, rechazos y avisos de tus
                solicitudes de pago y de tus solicitudes de catálogo.
              </Text>
            ) : (
              items.map((item) => (
                <Group
                  key={item.key}
                  gap={4}
                  wrap="nowrap"
                  align="flex-start"
                  className="rounded-lg p-2"
                  style={{
                    backgroundColor: item.noVista
                      ? "light-dark(var(--color-multiva-50), color-mix(in srgb, var(--color-multiva-600) 20%, transparent))"
                      : undefined,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => item.navegar?.()}
                    disabled={!item.navegar}
                    className="flex-1 text-left disabled:cursor-default"
                    style={{ cursor: item.navegar ? "pointer" : "default" }}
                  >
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <div className="pt-0.5">{item.icono}</div>
                      <Stack gap={2} className="flex-1">
                        {item.contenido}
                      </Stack>
                    </Group>
                  </button>
                  {item.eliminar && (
                    <IconAction
                      label={item.etiquetaEliminar}
                      size="sm"
                      loading={item.eliminando}
                      onClick={item.eliminar}
                    >
                      <IconTrash size={14} stroke={1.7} />
                    </IconAction>
                  )}
                </Group>
              ))
            )}
          </Stack>
        </div>
      </PopoverContent>
    </Popover>
  );
}
